<template>
  <div
    :class="[
      $style.actionTypeModalPreview,
      {
        [$style.actionTypeModalPreviewIsDark]: isDark,
        [$style.actionTypeModalPreviewIsMobile]: previewType === 'mobile',
      }
    ]"
  >
    <section
      :class="[
        $style.PFModal,
        {
          [$style.PFModalBgLeft]: backgroundPosition === 'left',
          [$style.PFModalBgTop]: backgroundPosition === 'top',
          [$style.PFModalBgRight]: backgroundPosition === 'right',
          [$style.PFModalWithoutBg]: backgroundImage === null,
          [$style.PFModalIsMobile]: previewType === 'mobile',
        }
      ]"
    >
      <button :class="$style.PFModalButtonClose">
        <CloseRounded :class="$style.PFModalButtonCloseIcon" />
      </button>

      <div
        v-if="backgroundImage"
        :class="$style.PFModalBackground"
      >
        <img
          :class="$style.PFModalBackgroundImage"
          :src="backgroundImagePreview"
          alt=""
        >
      </div>

      <div :class="$style.PFModalBody">
        <header :class="$style.PFModalHeader">
          <div
            v-if="logoFile"
            :class="[
              $style.PFModalHeaderLogo,
              {
                [$style.PFModalWithoutBg]: backgroundImage === null,
              }
            ]"
          >
            <img
              :class="$style.PFModalHeaderLogoImage"
              :src="logoFilePreview"
              alt=""
            >
          </div>
          <div v-else style="height: 20px;" />
          <div :class="$style.PFModalHeaderText">
            {{ text }}
          </div>
        </header>

        <div>
          <ActionTypeModalPreviewDualCard
            v-if="redirect === 'whatsapp-and-telegram'"
            :preview-type="previewType"
            :extra-text-enabled="extraTextEnabled"
            :whatsapp-button-text="whatsappButtonText"
            :whatsapp-qrcode-text="whatsappQrcodeText"
            :telegram-button-text="telegramButtonText"
            :telegram-qrcode-text="telegramQrcodeText"
            :is-dark="isDark"
          />
          <ActionTypeModalPreviewSoloCard
            v-else
            :preview-type="previewType"
            :redirect="redirect"
            :extra-text-enabled="extraTextEnabled"
            :whatsapp-button-text="whatsappButtonText"
            :whatsapp-qrcode-text="whatsappQrcodeText"
            :telegram-button-text="telegramButtonText"
            :telegram-qrcode-text="telegramQrcodeText"
            :is-dark="isDark"
          />
        </div>

        <footer
          v-if="branding"
          :class="$style.PFModalFooter"
        >
          Made on Yourgood Widget
        </footer>
      </div>
    </section>
  </div>
</template>

<script>
/**
 * UI
 */
import ActionTypeModalPreviewDualCard from '@/components/Actions/ActionPreview/ActionTypeModalPreview/ActionTypeModalPreviewDualCard.vue'
import ActionTypeModalPreviewSoloCard from '@/components/Actions/ActionPreview/ActionTypeModalPreview/ActionTypeModalPreviewSoloCard.vue'
/**
 * Static
 */
import CloseRounded from '@/assets/icons/CloseRounded.svg?inline'

export default {
  components: {
    ActionTypeModalPreviewDualCard,
    ActionTypeModalPreviewSoloCard,
    CloseRounded
  },
  props: {
    previewType: {
      type: String,
      default: 'desktop'
    },
    redirect: {
      type: String,
      default: 'whatsapp'
    },
    backgroundPosition: {
      type: String,
      default: 'left'
    },
    backgroundImage: {
      type: [
        Object,
        process.client ? window.File : Object
      ],
      default: null
    },
    logoFile: {
      type: [
        Object,
        process.client ? window.File : Object
      ],
      default: null
    },
    text: {
      type: String,
      default: ''
    },
    extraTextEnabled: {
      type: Boolean,
      default: false
    },
    whatsappButtonText: {
      type: String,
      default: ''
    },
    whatsappQrcodeText: {
      type: String,
      default: ''
    },
    telegramButtonText: {
      type: String,
      default: ''
    },
    telegramQrcodeText: {
      type: String,
      default: ''
    },
    isDark: {
      type: Boolean,
      default: false
    },
    branding: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    backgroundImagePreview () {
      if (!this.backgroundImage) { return null }
      return this.backgroundImage instanceof File
        ? URL.createObjectURL(this.backgroundImage)
        : this.backgroundImage.url
    },
    logoFilePreview () {
      if (!this.logoFile) { return null }
      return this.logoFile instanceof File
        ? URL.createObjectURL(this.logoFile)
        : this.logoFile.url
    }
  }
}
</script>

<style module lang="scss">
.actionTypeModalPreview {
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.23);

  &:not(.actionTypeModalPreviewIsDark) {
    .PFModal,
    .PFModalButtonClose {
      background-color: var(--white);
    }

    .PFModalHeaderText,
    .PFModalButtonClose {
      color: var(--primary);
    }

    .PFModalFooter {
      color: var(--grey-500);
    }
  }

  &.actionTypeModalPreviewIsDark {
    .PFModal,
    .PFModalButtonClose {
      background-color: var(--grey-900);
    }

    .PFModalHeaderText,
    .PFModalButtonClose {
      color: var(--white);
    }

    .PFModalFooter {
      color: var(--disabled);
    }
  }
}

.PFModal {
  all: unset;
  position: relative;
  display: flex;
  width: 100%;
  box-shadow: 0 10px 20px 1px rgba(33, 33, 33, 0.04);
  border-radius: calc(24px * var(--pf-modal-scale));
  overflow: hidden;

  &.PFModalBgLeft:not(.PFModalIsMobile),
  &.PFModalBgRight:not(.PFModalIsMobile) {
    height: calc(650px * var(--pf-modal-scale));
    max-width: calc(900px * var(--pf-modal-scale));

    .PFModalBackground {
      width: calc(380px * var(--pf-modal-scale));
    }

    .PFModalHeaderLogo {
      height: calc(56px * var(--pf-modal-scale));
      width: calc(160px * var(--pf-modal-scale));
    }
  }

  &.PFModalBgRight:not(.PFModalIsMobile) {
    flex-direction: row-reverse;
  }

  &.PFModalBgLeft:not(.PFModalIsMobile) {
    flex-direction: row;
  }

  &.PFModalBgTop,
  &.PFModalIsMobile {
    flex-direction: column;
    max-width: calc(620px * var(--pf-modal-scale));

    .PFModalBackground {
      width: 100%;
      height: calc(240px * var(--pf-modal-scale));
    }

    .PFModalHeaderLogo:not(.PFModalWithoutBg) {
      --s: calc(120px * var(--pf-modal-scale));

      position: relative;
      z-index: 1;
      margin-top: calc(-87px * var(--pf-modal-scale));
      border-radius: 50%;
      border: 1px solid var(--grey-200);
      width: var(--s);
      height: var(--s);
      display: flex;
      align-items: center;
      background-color: var(--white);
      overflow: hidden;
    }
  }

  &.PFModalWithoutBg {
    max-width: calc(620px * var(--pf-modal-scale)) !important;
    height: auto !important;
  }
}

.PFModalButtonClose {
  --s: calc(32px * var(--pf-modal-scale));

  all: unset;
  position: absolute;
  z-index: 2;
  top: calc(24px * var(--pf-modal-scale));
  right: calc(24px * var(--pf-modal-scale));
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--s);
  height: var(--s);
  border-radius: 50%;
  cursor: pointer;
}

.PFModalButtonCloseIcon {
  --s: calc(24px * var(--pf-modal-scale));

  all: unset;
  display: inline-block;
  width: var(--s);
  height: var(--s);
}

.PFModalBody {
  all: unset;
  position: relative;
  box-sizing: border-box;
  padding:
    calc(24px * var(--pf-modal-scale))
    calc(24px * var(--pf-modal-scale))
    calc(60px * var(--pf-modal-scale))
    calc(24px * var(--pf-modal-scale));
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: calc(24px * var(--pf-modal-scale));
}

.PFModalFooter {
  all: unset;
  width: 100%;
  position: absolute;
  left: 50%;
  bottom: calc(24px * var(--pf-modal-scale));
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  text-align: center;
  font-family: 'Roboto', sans-serif !important;
  font-weight: 400;
  font-size: calc(11px * var(--pf-modal-scale));
  line-height: calc(18px * var(--pf-modal-scale));
  letter-spacing: calc(0.1px * var(--pf-modal-scale));
}

.PFModalHeader {
  all: unset;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: calc(16px * var(--pf-modal-scale));
}

.PFModalHeaderLogo {
  all: unset;
  box-sizing: border-box;
  display: flex;
  height: calc(56px * var(--pf-modal-scale));
  width: calc(160px * var(--pf-modal-scale));
}

.PFModalHeaderLogoImage {
  all: unset;
  object-fit: cover;
  width: 100%;
}

.PFModalHeaderText {
  all: unset;
  display: inline-block;
  font-family: 'Roboto', sans-serif !important;
  font-style: normal;
  font-weight: 400;
  font-size: calc(16px * var(--pf-modal-scale));
  line-height: calc(28px * var(--pf-modal-scale));
  letter-spacing: calc(0.15px * var(--pf-modal-scale));
  word-wrap: anywhere !important;
  max-width: 100%;
}

.PFModalBackground {
  all: unset;
  display: flex;
  flex-shrink: 0;
  overflow: hidden;
}

.PFModalBackgroundImage {
  all: unset;
  object-fit: cover;
  width: 100%;
  height: 100%;
}
</style>
